/**
 * Mobile session DTOs.
 *
 * Returned by `GET /api/mobile/v1/session` after the mobile app exchanges a
 * Clerk JWT for the server-side session payload. The shape is intentionally
 * narrower than the full `User` row — the mobile UI only needs identity,
 * plan-state, and feature flags.
 *
 * Phase 02 builds the route that returns this shape.
 */
import { z } from "zod";
import { PlanStateSchema, PlanTierSchema } from "./plan-state";
import {
  ContractorPortalCompanyStrict,
  SitePortalRoleSchema,
} from "../entities/portal";
import { TeamRoleSchema } from "../entities/team";

/** Opt in to cache-scope fields while older installed strict parsers continue
 * receiving the original response. This changes representation, not access. */
export const MOBILE_SESSION_SCOPE_HEADER = "X-SiteBook-Session-Scope";
export const MOBILE_SESSION_SCOPE_VERSION = "1";

/** Optional roster freshness control shared by session and roster clients. */
export const EvacuationRosterEpochWire = z.number().int().nonnegative().optional();

/**
 * Roles surfaced to mobile. Mirrors Prisma's `UserRole` enum from
 * prisma/schema.prisma:
 *
 *   enum UserRole { BUILDER, HS_ADMIN }
 *
 * Mobile treats HS_ADMIN identically to BUILDER for v1 (no admin-only UI
 * ships in the first cut), but the field is surfaced so admin gating can be
 * added without a contracts bump.
 */
export const USER_ROLES = ["BUILDER", "HS_ADMIN"] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const UserRoleSchema = z.enum(USER_ROLES);

/**
 * Request body for `GET /api/mobile/v1/session` (currently empty — the
 * Authorization header carries the Clerk JWT). Reserved for future use
 * (e.g. client-build telemetry) without breaking the wire contract.
 */
export const SessionRequest = z.object({}).strict();

export type SessionRequest = z.infer<typeof SessionRequest>;

export const SessionUser = z
  .object({
    id: z
      .string()
      .describe(
        "The Site Book user id. NOTE: this mirrors the Clerk user id 1:1 " +
          "(format: `user_<base62>`). Schema does NOT enforce .uuid() because " +
          "the auto-create path in lib/auth.ts upserts User rows with the Clerk " +
          "id as the primary key — the Prisma @default(uuid()) is never used.",
      ),
    clerkId: z
      .string()
      .describe("Clerk user id (matches Clerk session)."),
    email: z.string().email(),
    name: z.string().nullable(),
    role: UserRoleSchema,
    membershipRole: TeamRoleSchema.nullable().describe(
      "The caller's role in their internal account. Null for contractor-only sessions.",
    ),
    companyId: z.string().nullable().describe(
      "Reserved for multi-seat company support. Always null in v1 (single-tenant per User).",
    ),
    companyName: z.string().nullable(),
  })
  .strict();

export type SessionUser = z.infer<typeof SessionUser>;

/**
 * `{ [featureId]: boolean }` — the server's own `canAccessFeature` verdict per
 * feature, keyed by the same snake_case ids the shared `featureCopy` table
 * uses (`f10_notify`, `cert_tracking`, …). This is ENTITLEMENT, which neither
 * `plan.tier` nor `plan.status` can express: it includes grandfathered grants
 * and (at project scope) setup-service elevation. Optional on the wire so a
 * new client parses older servers; a client that does not receive it must
 * fall back to advisory behaviour, never to a tier guess.
 */
export const FeatureAccessMap = z.record(z.string(), z.boolean());
export type FeatureAccessMap = z.infer<typeof FeatureAccessMap>;

export const SessionPlan = z.object({
  tier: PlanTierSchema.describe(
    "Effective internal Account plan tier, or the User tier for contractor-only sessions. Authoritative for UI labelling.",
  ),
  status: PlanStateSchema.describe(
    "Computed plan state used by mobile to gate writes. Read this, not tier, for gating decisions.",
  ),
  trialEndsAt: z.string().datetime().nullable().describe(
    "ISO-8601 UTC trial boundary from the internal Account owner, or the authenticated User for contractor-only sessions. Null when not on trial.",
  ),
  entitlements: FeatureAccessMap.optional().describe(
    "Account-scope feature access: canAccessFeature over every FEATURE id for the " +
    "resolved account plan plus the caller's grandfathered grants. Project-scope " +
    "elevation (setup service) is NOT included here — read GET /projects/{id} " +
    "featureAccess for that. Optional for older servers.",
  ),
  accountId: z.string().nullable().optional().describe(
    "The resolved internal Account, or null for a contractor-only session. " +
    "New clients request it with the session-scope capability header; optional " +
    "so those clients also parse older servers. Detects same-user account " +
    "changes for cache isolation, not authorization decisions.",
  ),
});
// Response readers tolerate future plan fields. Existing strict installs still
// need capability negotiation: relaxing THIS schema cannot update old binaries.

export type SessionPlan = z.infer<typeof SessionPlan>;

export const SessionFlags = z
  .object({
    mobileWritesEnabled: z
      .boolean()
      .describe(
        "Mirrors the `mobile_writes_enabled` server-side kill switch. When false, all write endpoints return WRITES_DISABLED — the mobile app SHOULD render write controls as disabled.",
      ),
  })
  .strict();

export type SessionFlags = z.infer<typeof SessionFlags>;

export const MobileAppModeSchema = z.enum([
  "INTERNAL",
  "CONTRACTOR",
  "MODE_PICKER",
]);

export type MobileAppMode = z.infer<typeof MobileAppModeSchema>;

export const ContractorPortalSession = z.object({
  portalUserId: z.string(),
  role: SitePortalRoleSchema,
  name: z.string(),
  company: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  projectCount: z.number().int().min(0),
});

export type ContractorPortalSession = z.infer<typeof ContractorPortalSession>;

/**
 * #2411 server-boundary variant. The contractor API parses the permissive
 * base off the wire; `SessionResponse` is a REQUIRED-boundary control and its
 * strict tree (see REQUIRED_STRICT_PATHS in
 * `__tests__/contracts/mobile-wire-strictness.test.ts`) embeds this form, so
 * the canonical strict control above `availableModes.contractor` survives.
 */
export const ContractorPortalSessionStrict = ContractorPortalSession.extend({
  company: ContractorPortalCompanyStrict.nullable(),
}).strict();

export const SessionAvailableModes = z
  .object({
    internal: z.boolean(),
    contractor: z.array(ContractorPortalSessionStrict),
  })
  .strict();

export type SessionAvailableModes = z.infer<typeof SessionAvailableModes>;

export const SessionResponse = z
  .object({
    user: SessionUser,
    plan: SessionPlan,
    flags: SessionFlags,
    mode: MobileAppModeSchema,
    availableModes: SessionAvailableModes,
    authorizationRevision: z.string().regex(/^[a-f0-9]{64}$/).optional().describe(
      "Live cache scope. A revision change requires discarding cached authorized reads. Optional for older servers; never a permission token.",
    ),
    evacuationRosterEpochs: z
      .record(z.string(), z.number().int().min(0))
      .optional()
      .describe(
        "Per-project evacuation-roster revocation epochs (#2432 second half): the " +
        "server's CURRENT integer epoch for every project the caller can read " +
        "rosters for (the exact eligible role, assignment, account entitlement, project toggle and global rollout). " +
        "Bumped on evacuation start/stand-down and safe-to-unaccounted correction, so cached roster " +
        "data stamped with an older epoch is stale. Optional for older " +
        "servers (and only sent under the session-scope header); when absent, " +
        "the bounded lease fallback applies. When present, omission of a project " +
        "key means no roster is in scope for that session. Never a permission " +
        "token: the route still enforces live authorization.",
      ),
  })
  .strict();

export type SessionResponse = z.infer<typeof SessionResponse>;
